<template>
  <b-card
    v-show="isVisible"
    class="comment-textarea mb-2"
  >
    <label class="text-muted font-sm">{{ inputLabel }}</label>

    <div
      class="comment-input__editor"
      :class="{ 'comment-input__editor--dragging': isDragging }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <RichTextEditor
        ref="inputComment"
        v-model="commentContent"
        allow-images
        :allow-library-browse="false"
        placeholder="Write a comment, or drop an image or video here"
        @input="setCommentText"
        @ready="onEditorReady"
        @pick-upload="pickInlineMedia"
      />

      <div class="comment-input__bar">
        <button
          type="button"
          class="comment-input__attach"
          @click="pickFiles"
        >
          <i class="bi bi-paperclip comment-input__attach-icon" />
          <span>Attach files</span>
        </button>
        <span
          v-if="isUploading"
          class="comment-input__uploading"
        >
          <b-spinner small /> Uploading…
        </span>
        <span
          v-else
          class="comment-input__formats"
        >images · video · .pdf · .docx</span>
      </div>
    </div>

    <div
      v-if="attachments.length"
      class="comment-input__chips mt-2"
    >
      <span
        v-for="item in attachments"
        :key="item.id"
        class="comment-input__chip"
      >
        <i :class="['bi', referenceChipIconClass(item)]" />
        <span class="comment-input__chip-name">{{ item.name }}</span>
        <button
          type="button"
          class="comment-input__chip-remove"
          title="Remove file"
          @click="removeAttachment(item)"
        >
          <b-icon icon="x" />
        </button>
      </span>
    </div>

    <div class="d-flex justify-content-between align-items-center my-1">
      <div>
        <b-button-group size="sm">
          <template v-if="recording && !commentEditing">
            <b-button
              v-b-tooltip.hover
              variant="outline-info cursor-pointer"
              title="Start screen recording"
              :disabled="recording.recordButtonDisabled"
              @click="startRecording('screen')"
            >
              <b-icon icon="mic" />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="outline-info cursor-pointer"
              title="Start recording"
              :disabled="recording.recordButtonDisabled"
              @click="startRecording('video')"
            >
              <b-icon icon="camera-reels" />
            </b-button>
            <b-button
              v-if="recording.saveVideoModel"
              v-b-tooltip.hover
              variant="outline-success cursor-pointer"
              title="Upload video"
              @click="$emit('submit-video')"
            >
              <b-icon icon="upload" />
            </b-button>
            <b-button
              v-if="recording.stopVideoModel"
              v-b-tooltip.hover
              variant="outline-danger cursor-pointer"
              title="Stop recording"
              @click="$emit('stop-recording')"
            >
              <b-icon icon="stop-circle" />
            </b-button>
            <b-button
              v-if="recording.deleteVideoModel"
              v-b-tooltip.hover
              variant="outline-danger cursor-pointer"
              title="Delete video"
              @click="$emit('delete-recording')"
            >
              <b-icon icon="trash" />
            </b-button>
          </template>
          <b-button
            v-b-tooltip.hover
            variant="outline-primary"
            title="Save comment"
            :disabled="isSubmitting || isUploading"
            @click="handleSubmit"
          >
            <b-spinner
              v-if="isSubmitting"
              small
            />
            <b-icon
              v-else
              icon="check2"
            />
          </b-button>
          <b-button
            v-b-tooltip.hover
            variant="outline-danger"
            title="Discard comment"
            @click="$root.$emit('close-comment')"
          >
            <b-icon
              icon="x"
              scale="1.5"
            />
          </b-button>
        </b-button-group>
      </div>
      <b-checkbox
        v-model="isDraft"
        switch
      >
        Draft
      </b-checkbox>
    </div>

    <input
      ref="fileInput"
      type="file"
      multiple
      class="d-none"
      :accept="accept"
      @change="onFilesPicked"
    >
    <input
      ref="inlineInput"
      type="file"
      multiple
      class="d-none"
      :accept="inlineAccept"
      @change="onInlineMediaPicked"
    >

    <template v-if="recording">
      <p
        v-if="recording.recordingTextModel"
        id="recordingText"
        :class="recording.messageClass ? 'alert alert-success' : 'alert alert-danger'"
      >
        {{ recording.recordingText }}
        <span v-if="recording.recordTimer">{{ recording.timer }} s</span>
      </p>
      <div class="vidoe-container">
        <p v-if="recording.startUpload">
          Uploading...
        </p>
        <video
          v-if="recording.videoUrl"
          class="float-right"
          width="200"
          controls
          :src="recording.videoUrl"
        />
      </div>
    </template>
  </b-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import toastr from 'toastr'

import RichTextEditor from '@frontend/components/common/editors/RichTextEditor.vue'
import mediaApi from '@frontend/services/api/media'
import {
  collectFilesFromDrop,
  collectFilesFromInput,
  isVideoFile,
  newPendingAttachment,
  referenceChipIconClass,
  revokePendingPreview,
} from '@frontend/utils/fileAttachments'
import { isPlaceableImageFile } from '@frontend/components/modules/file-browser/helpers'

const ACCEPT = '.pptx,.docx,.pdf,.rar,.zip,.psd,.txt,.md,.png,.jpg,.jpeg,.webp,.gif,.mp4,.mov,.webm,.avi,image/*,video/*';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mirrors HistoryController::COMMENT_DENIED_EXTENSIONS; the two lists have to move together.
const DENIED_EXTENSIONS = ['svg', 'svgz', 'xml'];
const INLINE_ACCEPT = 'image/*,video/*,.mp4,.mov,.webm,.avi';

export default {
  name: 'CommentInput',

  components: {
    RichTextEditor,
  },

  props: {
    commentsCollection: {
      type: Array,
      default: () => [],
    },
    editedComment: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: null,
    },
    // Set by the CommentItem hosting the inline editor; page-level boxes leave it null and hide.
    editingCommentId: {
      type: [String, Number],
      default: null,
    },
    // Null hides the recording controls; a host that cannot drive videojs-record must not show them.
    recording: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    commentContent: '',
    isSubmitting: false,
    isDragging: false,
    accept: ACCEPT,
    inlineAccept: INLINE_ACCEPT,
    attachments: [],
    uploadCount: 0,
    quill: null,
  }),

  computed: {
    ...mapGetters('project', [
      'showCommentBox',
      'commentText',
      'commentStatus',
      'commentEditing',
      'commentReplying',
    ]),
    isVisible () {
      if (this.commentEditing) {
        return this.editingCommentId === this.commentEditing
      }

      return this.show ?? this.showCommentBox
    },
    isUploading () {
      return this.uploadCount > 0
    },
    isDraft: {
      get () {
        return this.commentStatus === 'draft'
      },
      set (val) {
        this.updateCommentStatus(val ? 'draft' : null)
      },
    },
    inputLabel () {
      if (this.commentReplying) {
        return 'Replying to comment'
      } else if (this.commentEditing) {
        return 'Editing comment'
      } else {
        return 'Adding a new comment'
      }
    },
  },

  watch: {
    showCommentBox: {
      immediate: true,
      handler (val) {
        !this.show && this.showHandler(val);
        if (this.commentEditing) {
          this.commentContent = this.editedComment || '';
        } else {
          this.commentContent = '';
          this.clearAttachments();
        }
      },
    },
    show: {
      immediate: true,
      handler (val) {
        this.showHandler(val);
      },
    },
    commentText: {
      immediate: true,
      handler (val) {
        if (this.commentEditing && val) {
          this.commentContent = val;
        }
      },
    },
  },

  mounted() {
    this.$root.$on('comment-submit-done', this.onSubmitDone);
  },

  beforeDestroy() {
    this.$root.$off('comment-submit-done', this.onSubmitDone);
    this.releaseAttachments();
    if (this.quill) {
      this.quill.root.removeEventListener('keydown', this.preventEnterBubble);
      this.quill.root.removeEventListener('paste', this.onPaste, true);
    }
  },

  methods: {
    ...mapActions('project', [
      'updateCommentText',
      'updateCommentAttachments',
      'updateCommentStatus',
    ]),
    referenceChipIconClass,

    onEditorReady (quill) {
      this.quill = quill;
      quill.root.addEventListener('keydown', this.preventEnterBubble);
      // Capture phase: Quill's own paste listener is here and would hotlink the source <img> first.
      quill.root.addEventListener('paste', this.onPaste, true);
    },

    // Stops Enter reaching ShareProofVideo's document submit; stopPropagation only, so Quill still breaks the line.
    preventEnterBubble (event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.stopPropagation();
      }
    },

    showHandler (val) {
      if (!val || !this.isVisible) {
        return
      }
      this.$nextTick(() => {
        const quill = this.$refs.inputComment?.quill;
        if (quill) {
          quill.focus();
          if (this.commentEditing) {
            quill.setSelection(quill.getLength(), 0);
          }
        }
      })
    },

    setCommentText(text) {
      const cleanedText = (text || '').split(/\s+/).join(' ');
      this.updateCommentText(cleanedText);
      this.tagEmail(cleanedText);
    },

    tagEmail(text) {
      const emails = text
        .split(/\s+/)
        .filter((word) => word.startsWith('@'))
        .map((word) => word.slice(1).trim())
        .filter((email) => EMAIL_PATTERN.test(email));

      // $root, not an emit: the inline editor sits several levels below the page that owns the list.
      this.$root.$emit('comment-tagged-emails', [...new Set(emails)]);
    },

    pickFiles () {
      this.$refs.fileInput && this.$refs.fileInput.click();
    },

    pickInlineMedia () {
      this.$refs.inlineInput && this.$refs.inlineInput.click();
    },

    onInlineMediaPicked (event) {
      const entries = collectFilesFromInput(event.target.files);
      event.target.value = '';
      this.addFiles(entries.map((entry) => entry.file), { allowInline: true });
    },

    onFilesPicked (event) {
      const entries = collectFilesFromInput(event.target.files);
      event.target.value = '';
      this.addFiles(entries.map((entry) => entry.file));
    },

    async onDrop (event) {
      this.isDragging = false;
      const entries = await collectFilesFromDrop(event);
      this.addFiles(entries.map((entry) => entry.file), { allowInline: true });
    },

    onPaste (event) {
      const files = Array.from(event.clipboardData?.files || []);
      if (!files.length) {
        return;
      }
      event.preventDefault();
      this.addFiles(files, { allowInline: true });
    },

    // Same split as BriefComposerDropzone: drop/paste embeds, "Attach files" always chips.
    async addFiles (files, { allowInline = false } = {}) {
      for (const file of files) {
        if (this.isDeniedFile(file)) {
          toastr.error(`${file.name || 'That file'} cannot be attached to a comment.`);
          continue;
        }
        if (allowInline && (isPlaceableImageFile(file) || isVideoFile(file))) {
          // Sequential: a parallel upload would insert at an index captured before the last embed.
          await this.embedMedia(file);
        } else {
          this.addChip(file);
        }
      }
    },

    isDeniedFile (file) {
      const name = (file && file.name) || '';
      const extension = name.includes('.') ? name.split('.').pop().toLowerCase() : '';

      return DENIED_EXTENSIONS.includes(extension) || (file.type || '').includes('svg');
    },

    addChip (file) {
      this.attachments.push(newPendingAttachment(file));
      this.syncAttachments();
    },

    removeAttachment (item) {
      revokePendingPreview(item);
      this.attachments = this.attachments.filter((entry) => entry.id !== item.id);
      this.syncAttachments();
    },

    clearAttachments () {
      this.releaseAttachments();
      this.syncAttachments();
    },

    releaseAttachments () {
      this.attachments.forEach(revokePendingPreview);
      this.attachments = [];
    },

    syncAttachments () {
      const files = this.attachments.map((item) => item.file);
      this.updateCommentAttachments(files);
      this.$emit('update:attachments', files);
    },

    async embedMedia (file) {
      const quill = this.quill;
      if (!quill) {
        return;
      }

      this.uploadCount += 1;
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await mediaApi.uploadCommentMedia(formData);
        // Read the caret only now: the user can keep typing while the file uploads.
        const range = quill.getSelection();
        const cursor = range ? range.index : Math.max(0, quill.getLength() - 1);
        quill.insertEmbed(cursor, data.kind === 'video' ? 'briefVideo' : 'image', data.url, 'user');
        quill.setSelection(cursor + 1, 0, 'silent');
      } catch (error) {
        const message = error?.response?.data?.message;
        toastr.error(message || `Could not embed ${file.name || 'file'}; attached it instead.`);
        this.addChip(file);
      } finally {
        this.uploadCount -= 1;
      }
    },

    startRecording(type) {
      this.$emit('startRecording', { type });
    },

    onSubmitDone () {
      this.isSubmitting = false;
    },

    handleSubmit () {
      this.isSubmitting = true;
      this.setCommentText(this.commentContent);
      // One store slot for every box: the one submitting claims it, or a file staged elsewhere rides along.
      this.syncAttachments();
      this.$root.$emit('submit-comment');
      const enterEvent = new KeyboardEvent('keydown', { keyCode: 13, which: 13, key: 'Enter' });
      document.dispatchEvent(enterEvent);
    },
  },
}
</script>

<style scoped>
.comment-input__editor {
  position: relative;
}

.comment-input__editor--dragging {
  outline: 2px dashed var(--primary, #28a745);
  outline-offset: 4px;
}

.comment-input__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 8px;
  padding: 4px 8px;
  border: 1px solid hsl(214.3, 31.8%, 91.4%);
  border-radius: 0 0 0.375rem 0.375rem;
  background: hsl(210 40% 96.1% / 0.3);
}

.comment-input__editor ::v-deep .ql-toolbar.ql-snow .ql-formats {
  flex-wrap: wrap;
}

.comment-input__editor ::v-deep .ql-container {
  border-bottom: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.comment-input__editor ::v-deep .ql-editor {
  margin-bottom: 0;
}

.comment-input__attach {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #6c757d;
  font-size: 12px;
  cursor: pointer;
}

.comment-input__attach:hover {
  background: #f1f3f5;
  color: var(--primary, #28a745);
}

.comment-input__attach-icon {
  font-size: 14px;
}

.comment-input__formats,
.comment-input__uploading {
  color: #6c757d;
  font-size: 11px;
  white-space: nowrap;
}

.comment-input__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.comment-input__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 240px;
  padding: 4px 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #f8f9fa;
  font-size: 12px;
}

.comment-input__chip-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.comment-input__chip-remove {
  padding: 0;
  border: 0;
  background: none;
  color: #6c757d;
  line-height: 1;
}

.comment-input__chip-remove:hover {
  color: #dc3545;
}
</style>
